import { useEffect, useMemo, useRef } from 'react';

import { Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';

import { formatMaxSize, validateShapefileCumulativeSize } from '../../constants';

// Styling lives with the upload flow it was extracted from; both modals that
// consume this component already import the same stylesheet.
import '../Modals/UploadModal/UploadModal.scss';

const { Dragger } = Upload;

const getExtension = (name: string): string => name.slice(name.lastIndexOf('.') + 1).toLowerCase();

const getBaseName = (name: string): string => {
  const lastDot = name.lastIndexOf('.');
  return lastDot === -1 ? name : name.slice(0, lastDot).toLowerCase();
};

interface SlotRow {
  key: string;
  status: 'missing' | 'success' | 'error';
  label: string;
  file?: File;
  reason?: 'type' | 'name';
}

interface FileDraggerProps {
  /** Extensions the dataset expects, e.g. shapefile parts or a single type. */
  requiredExtensions: string[];
  /** Shapefile-style multi-slot set vs. a single primary file. */
  isMultiPart: boolean;
  /** `accept` for the OS file picker, e.g. ".shp,.dbf". */
  accept: string;
  /** Placeholder shown in an empty single-file slot (e.g. the file type). */
  placeholderLabel: string;
  selectedFiles: FileList | File[] | null;
  /** Same event shape the upload hook already consumes. */
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Fired as the "all required slots satisfied" state changes. */
  onValidityChange: (canSubmit: boolean) => void;
  disabled?: boolean;
  /** Per-file size cap; files above it are rejected with a toast. */
  maxBytesFor?: (file: File) => number;
  /** Extra async validation (e.g. GeoTIFF world-file check). */
  validate?: (files: File[]) => Promise<string | null>;
  /** Banner error text owned by the parent (e.g. validate output). */
  errorMessage?: string;
  setErrorMessage?: (message: string) => void;
}

export const FileDragger = ({
  requiredExtensions,
  isMultiPart,
  accept,
  placeholderLabel,
  selectedFiles,
  handleFileChange,
  onValidityChange,
  disabled = false,
  maxBytesFor,
  validate,
  errorMessage,
  setErrorMessage,
}: FileDraggerProps) => {
  const filesRef = useRef<File[]>([]);
  useEffect(() => {
    filesRef.current = selectedFiles ? Array.from(selectedFiles) : [];
  }, [selectedFiles]);

  const applyFiles = (newFiles: File[]) => {
    filesRef.current = newFiles;
    const dataTransfer = new DataTransfer();
    newFiles.forEach((file) => dataTransfer.items.add(file));
    const event = {
      target: { files: dataTransfer.files },
    } as React.ChangeEvent<HTMLInputElement>;

    if (validate) {
      validate(newFiles).then((error) => setErrorMessage?.(error ?? ''));
    }
    handleFileChange(event);
  };

  const handleRemoveFile = (fileToRemove: File) => {
    applyFiles(filesRef.current.filter((f) => f !== fileToRemove));
  };

  const clearShapefileFiles = () => {
    // Keep the ref and the parent-controlled FileList in sync when a size
    // violation occurs; otherwise later individual selections can reuse stale
    // sibling parts from the rejected set.
    filesRef.current = [];
    const dataTransfer = new DataTransfer();
    const event = {
      target: { files: dataTransfer.files },
    } as React.ChangeEvent<HTMLInputElement>;
    if (validate) {
      validate([]).then((error) => setErrorMessage?.(error ?? ''));
    }
    handleFileChange(event);
  };

  const handleBeforeUpload = (file: File) => {
    if (maxBytesFor) {
      const maxBytes = maxBytesFor(file);
      if (!isMultiPart && file.size > maxBytes) {
        message.error(`${file.name} exceeds the ${formatMaxSize(maxBytes)} limit.`);
        return Upload.LIST_IGNORE;
      }
    }

    const currentFiles = filesRef.current;
    const fileExt = getExtension(file.name);

    if (isMultiPart) {
      const applyShapefileFiles = (nextFiles: File[]) => {
        const error = validateShapefileCumulativeSize(nextFiles, requiredExtensions);
        if (error) {
          message.error(error);
          clearShapefileFiles();
          return;
        }
        applyFiles(nextFiles);
      };

      // One file per extension slot: a new file for an occupied slot replaces it.
      const slotConflict = currentFiles.find((f) => getExtension(f.name) === fileExt);
      if (slotConflict) {
        applyShapefileFiles(currentFiles.filter((f) => f !== slotConflict).concat(file));
        return Upload.LIST_IGNORE;
      }
      applyShapefileFiles([...currentFiles, file]);
      return Upload.LIST_IGNORE;
    }

    // Single-file: latest drop wins.
    applyFiles([file]);
    return Upload.LIST_IGNORE;
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    showUploadList: false,
    openFileDialogOnClick: false,
    disabled,
    accept,
    beforeUpload: handleBeforeUpload,
  };

  const selectFilesProps: UploadProps = {
    ...uploadProps,
    openFileDialogOnClick: true,
  };

  const slotRows = useMemo((): SlotRow[] => {
    const currentFiles: File[] = selectedFiles ? Array.from(selectedFiles) : [];

    if (isMultiPart) {
      const used = new Set<File>();

      // Pass 1: match each required extension to a file.
      const slots = requiredExtensions.map((ext) => {
        const file = currentFiles.find((f) => !used.has(f) && getExtension(f.name) === ext);
        if (file) used.add(file);
        return { ext, file };
      });

      // Pass 2: fill still-empty slots with leftover mismatched files, so a
      // wrong-type drop occupies the open row instead of appending below it.
      let leftovers = currentFiles.filter((f) => !used.has(f));
      const finalSlots = slots.map((slot) => {
        if (slot.file || leftovers.length === 0) {
          return { ...slot, substituted: false };
        }
        const substitute = leftovers[0];
        leftovers = leftovers.slice(1);
        used.add(substitute);
        return { ext: slot.ext, file: substitute, substituted: true };
      });

      // Components must share the same filename prefix; the expected base is the
      // one shared by most matched files. A replacement set may use a different
      // name than the original as long as its own parts agree.
      const realMatches = finalSlots.filter((s) => s.file && !s.substituted);
      const bases = realMatches.map((s) => getBaseName(s.file!.name));
      const counts: Record<string, number> = {};
      bases.forEach((b) => (counts[b] = (counts[b] ?? 0) + 1));
      const majorityBase = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];

      const rows: SlotRow[] = finalSlots.map((slot) => {
        if (!slot.file) {
          return {
            key: slot.ext,
            status: 'missing',
            label: `PLEASE UPLOAD .${slot.ext.toUpperCase()} FILE`,
          };
        }
        if (slot.substituted) {
          return {
            key: slot.file.name,
            status: 'error',
            label: `${slot.file.name} (UNEXPECTED)`,
            file: slot.file,
            reason: 'type',
          };
        }
        const nameMatches = getBaseName(slot.file.name) === majorityBase;
        return {
          key: slot.file.name,
          status: nameMatches ? 'success' : 'error',
          label: slot.file.name,
          file: slot.file,
          ...(nameMatches ? {} : { reason: 'name' as const }),
        };
      });

      leftovers.forEach((file) => {
        rows.push({
          key: file.name,
          status: 'error',
          label: `${file.name} (UNEXPECTED)`,
          file,
          reason: 'type',
        });
      });

      return rows;
    }

    // Single-type: one primary slot, anything else is unexpected.
    const primary = currentFiles.find((f) => requiredExtensions.includes(getExtension(f.name)));
    const rest = currentFiles.filter((f) => f !== primary);

    const rows: SlotRow[] = [
      primary
        ? {
            key: primary.name,
            status: 'success',
            label: primary.name,
            file: primary,
          }
        : {
            key: 'placeholder',
            status: 'missing',
            label: `PLEASE UPLOAD ${placeholderLabel} FILE`,
          },
    ];

    rest.forEach((file) => {
      rows.push({
        key: file.name,
        status: 'error',
        label: `${file.name} (UNEXPECTED)`,
        file,
      });
    });

    return rows;
  }, [selectedFiles, requiredExtensions, isMultiPart, placeholderLabel]);

  const canSubmit = slotRows.length > 0 && slotRows.every((row) => row.status === 'success');

  useEffect(() => {
    onValidityChange(canSubmit);
  }, [canSubmit, onValidityChange]);

  const hasSlotError = slotRows.some((row) => row.status === 'error');

  // Shapefile-specific validation feedback (US-109513 / US-109517). Only once
  // the user has added files, and only while the set is still invalid.
  const hasFiles = (selectedFiles?.length ?? 0) > 0;
  const shapefileError =
    isMultiPart && hasFiles && !canSubmit
      ? slotRows.some((row) => row.reason === 'name')
        ? 'All shapefile components must have matching filenames.'
        : slotRows.some((row) => row.status === 'missing')
          ? `Incomplete shapefile. All required files (${requiredExtensions.map((ext) => `.${ext}`).join(', ')}) must be uploaded together.`
          : ''
      : '';
  const bannerMessage = errorMessage || shapefileError || (hasSlotError ? 'Remove incorrect file(s) and reupload.' : '');

  return (
    <div className="upload-file">
      <div className="upload-file__content">
        <div className="upload-file__slots">
          {slotRows.map((row) => (
            <div key={row.key} className={`upload-file__slot upload-file__slot--${row.status}`}>
              {row.file ? (
                <>
                  <span className="upload-file__slot-text" title={row.label}>
                    {row.label}
                  </span>
                  <CloseCircleOutlined className="upload-file__slot-remove" onClick={() => handleRemoveFile(row.file!)} />
                </>
              ) : (
                row.label
              )}
            </div>
          ))}
        </div>
        <div className="upload-file__dropzone-column">
          <Dragger {...uploadProps} className="upload-file__dropzone" />
          <Upload {...selectFilesProps}>
            <button type="button" className="upload-file__select-btn" disabled={disabled}>
              SELECT FILES LOCALLY
            </button>
          </Upload>
        </div>
      </div>

      {bannerMessage && (
        <div className="upload-file__error-banner">
          <img src="/assets/warning-sign-icon.svg" alt="Warning" className="upload-file__error-icon" />
          <div className="upload-file__error-body">
            <span className="upload-file__error-title">ERROR! FILE TYPE UNCOMPATIBLE</span>
            <span className="upload-file__error-message">{bannerMessage}</span>
          </div>
        </div>
      )}
    </div>
  );
};
